import React, { useState } from "react";

// ── Attribute block ───────────────────────────────────────────────────────────
// Expects { attributeName, dataType, data, notes? } — mirrors ProductDetailModal's AttributeBlock
function AttributeBlock({ attr, language }) {
    const { attributeName, dataType, data, notes } = attr;

    return (
        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-orange-500/5 border-b border-orange-500/20">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <h4 className="text-sm font-semibold text-gray-700 capitalize">
                    {attributeName}
                </h4>
            </div>
            <div className="p-4">
                {/* list → bullet list with orange chevron */}
                {dataType === "list" && Array.isArray(data) && (
                    <ul className="space-y-2">
                        {data.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <svg
                                    className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span className="text-sm text-gray-700">
                                    {String(item)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {/* table → full table with column headers */}
                {dataType === "table" &&
                    Array.isArray(data) &&
                    data.length > 0 &&
                    (() => {
                        // Union of all keys across every row so no column is missed
                        const cols = [...new Set(data.flatMap(Object.keys))];
                        return (
                            <div className="overflow-x-auto -mx-1">
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="bg-gray-100">
                                            {cols.map((col) => (
                                                <th
                                                    key={col}
                                                    className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200 capitalize whitespace-nowrap"
                                                >
                                                    {col.replace(/_/g, " ")}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((row, i) => {
                                            return (
                                                <tr
                                                    key={i}
                                                    className={
                                                        i % 2 === 0
                                                            ? "bg-white"
                                                            : "bg-gray-50"
                                                    }
                                                >
                                                    {cols.map((col) => {
                                                        const val = row[col];
                                                        const display =
                                                            val === null ||
                                                            val === undefined
                                                                ? "—"
                                                                : typeof val ===
                                                                    "object"
                                                                  ? JSON.stringify(
                                                                        val,
                                                                    )
                                                                  : String(val);
                                                        return (
                                                            <td
                                                                key={col}
                                                                className="px-3 py-2 text-gray-700 border-b border-gray-100 whitespace-nowrap"
                                                            >
                                                                {display}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}

                {/* table notes */}
                {dataType === "table" &&
                    Array.isArray(notes) &&
                    notes.length > 0 && (
                        <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-xs font-semibold text-yellow-700 mb-1">
                                {language === "zh" ? "備註" : "Notes"}
                            </p>
                            <ul className="space-y-0.5">
                                {notes.map((note, i) => (
                                    <li
                                        key={i}
                                        className="text-xs text-yellow-800 flex items-start gap-1.5"
                                    >
                                        <span className="shrink-0 mt-0.5 text-yellow-500">
                                            •
                                        </span>
                                        {note}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                {/* string / anything else → plain text */}
                {dataType !== "list" && dataType !== "table" && (
                    <p className="text-sm text-gray-700">{String(data)}</p>
                )}
            </div>
        </div>
    );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
function CompetitorProductModal({ product, language, onClose }) {
    const [selectedImage, setSelectedImage] = useState(null);

    if (!product) return null;

    const hasAttributes =
        Array.isArray(product.attributes) && product.attributes.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />
            <div className="relative bg-white shadow-2xl w-full max-w-[900px] max-h-[90vh] flex flex-col rounded-2xl overflow-hidden">
                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <svg
                                className="w-4 h-4 text-orange-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                />
                            </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg font-bold text-gray-900 truncate uppercase tracking-wide">
                                {product.model_id}
                            </h2>
                            <div className="flex items-center gap-2 mt-0.5">
                                {product.competitor_string && (
                                    <span className="px-2 py-0.5 bg-orange-50 border border-orange-200 rounded text-xs text-orange-600 font-medium">
                                        {product.competitor_string}
                                    </span>
                                )}
                                {product.category_name && (
                                    <>
                                        <span className="text-gray-300">|</span>
                                        <span className="text-xs text-gray-500">
                                            {product.category_name}
                                        </span>
                                    </>
                                )}
                                {product.sub_category_name && (
                                    <>
                                        <span className="text-gray-300">-</span>
                                        <span className="text-xs text-gray-500">
                                            {product.sub_category_name}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all ml-4 flex-shrink-0"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* ── Scrollable Body ─────────────────────────────────────── */}
                <div className="overflow-y-auto flex-1">
                    <div className="p-6 space-y-6">
                        {/* Info row: Producer + Product ID */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-start gap-2">
                                <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                </svg>
                                <div>
                                    <p className="text-xs text-gray-500">
                                        {language === "zh"
                                            ? "製造商"
                                            : "Producer"}
                                    </p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {product.competitor_string || "—"}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                    />
                                </svg>
                                <div>
                                    <p className="text-xs text-gray-500">
                                        {language === "zh"
                                            ? "產品編號"
                                            : "Product ID"}
                                    </p>
                                    <p className="text-sm font-mono text-gray-900">
                                        #{product.product_id}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {product.description && (
                            <div className="flex items-start gap-2">
                                <svg
                                    className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 6h16M4 12h16M4 18h7"
                                    />
                                </svg>
                                <div>
                                    <p className="text-xs text-gray-500 mb-1">
                                        {language === "zh"
                                            ? "描述"
                                            : "Description"}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                        {product.description}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Compliance Badges */}
                        <div className="flex flex-wrap gap-2">
                            {product.rohs_compliant && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-600 rounded-lg text-xs font-medium">
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    RoHS{" "}
                                    {language === "zh" ? "符合" : "Compliant"}
                                </span>
                            )}
                            {product.reach_compliant && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-600 rounded-lg text-xs font-medium">
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    REACH{" "}
                                    {language === "zh" ? "符合" : "Compliant"}
                                </span>
                            )}
                            {product.ul_comparable && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg text-xs font-medium">
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    UL{" "}
                                    {language === "zh" ? "可比" : "Comparable"}
                                </span>
                            )}
                        </div>

                        {/* PDF Link */}
                        {product.pdf_url && (
                            <a
                                href={product.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all group"
                            >
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                                    <svg
                                        className="w-5 h-5 text-orange-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-gray-900 font-medium text-sm group-hover:text-orange-500 transition-colors">
                                        {language === "zh"
                                            ? "產品規格書"
                                            : "Product Datasheet"}
                                    </div>
                                    <div className="text-gray-400 text-xs mt-0.5">
                                        {language === "zh"
                                            ? "點擊下載 PDF"
                                            : "Click to download PDF"}
                                    </div>
                                </div>
                                <svg
                                    className="w-4 h-4 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-0.5 transition-all flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </a>
                        )}

                        {/* Images */}
                        {Array.isArray(product.images) &&
                            product.images.length > 0 && (
                                <div>
                                    <p className="text-xs text-gray-500 font-medium mb-2">
                                        {language === "zh"
                                            ? "產品圖片"
                                            : "Product Images"}
                                        <span className="ml-1 text-gray-400">
                                            ({product.images.length})
                                        </span>
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {product.images.map((src, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() =>
                                                    setSelectedImage(src)
                                                }
                                                className="block w-20 h-20 rounded-xl overflow-hidden border border-gray-200 hover:border-orange-400 hover:shadow-md transition-all flex-shrink-0"
                                            >
                                                <img
                                                    src={src}
                                                    alt={`${product.model_id} img ${i + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                        {selectedImage && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center"
                                onClick={() => setSelectedImage(null)}
                            >
                                <div className="absolute inset-0 bg-black/70" />
                                <img
                                    src={selectedImage}
                                    alt="Selected product"
                                    className="relative max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg object-contain"
                                />
                            </div>
                        )}

                        {/* Attributes */}
                        {hasAttributes && (
                            <div className="space-y-4">
                                {product.attributes.map((attr, i) => (
                                    <AttributeBlock
                                        key={attr.attributeName ?? i}
                                        attr={attr}
                                        language={language}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer ──────────────────────────────────────────────── */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
                    >
                        {language === "zh" ? "關閉" : "Close"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CompetitorProductModal;
